// Helpers for working with Formik error objects

type AnyObject = Record<string, any>;

const isPlainObject = (value: unknown): value is AnyObject =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * Build a Formik `touched` object from a Formik `errors` object.
 * Marks all leaf error paths as `true`, so the UI shows validation messages.
 */
export function touchedFromErrors(errors: unknown): AnyObject {
  if (!errors) return {};
  if (typeof errors === "string") return true as any;
  if (Array.isArray(errors)) {
    return errors.map((e) => touchedFromErrors(e));
  }
  if (!isPlainObject(errors)) return {};

  const out: AnyObject = {};
  for (const [key, value] of Object.entries(errors)) {
    if (typeof value === "string") out[key] = true;
    else if (Array.isArray(value)) out[key] = value.map((v) => touchedFromErrors(v));
    else if (isPlainObject(value)) out[key] = touchedFromErrors(value);
  }
  return out;
}

export function hasErrors(errors: unknown): boolean {
  if (!errors) return false;
  if (typeof errors === "string") return true;
  if (Array.isArray(errors)) return errors.some((e) => hasErrors(e));
  if (isPlainObject(errors)) return Object.values(errors).some((v) => hasErrors(v));
  return false;
}

// [2026-02-13] SCROLL_TO_FIRST_ERROR: Extract the first error field name from nested errors
function getFirstErrorFieldName(errors: unknown, prefix = ""): string | null {
  if (!isPlainObject(errors)) return null;
  for (const key of Object.keys(errors)) {
    const path = prefix ? `${prefix}.${key}` : key;
    const value = (errors as AnyObject)[key];
    if (typeof value === "string") return path;
    if (isPlainObject(value)) {
      const nested = getFirstErrorFieldName(value, path);
      if (nested) return nested;
    }
    if (Array.isArray(value)) {
      for (let i = 0; i < value.length; i++) {
        const nested = getFirstErrorFieldName(value[i], `${path}[${i}]`);
        if (nested) return nested;
      }
    }
  }
  return null;
}

/**
 * Scroll the viewport to the first Formik error field and focus it.
 * Uses the field `name` attribute set by formik.getFieldProps().
 */
export function scrollToFirstError(errors: unknown): void {
  const fieldName = getFirstErrorFieldName(errors);
  if (!fieldName) return;

  // Small delay so touched state renders the red border first
  setTimeout(() => {
    const el =
      document.querySelector<HTMLElement>(`[name="${fieldName}"]`) ||
      document.getElementById(fieldName);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.focus({ preventScroll: true });
  }, 100);
}

